// Timezone math built entirely on Intl.DateTimeFormat with an explicit `timeZone` option.
//
// We deliberately avoid date-fns-tz's toZonedTime/fromZonedTime here: those functions
// return a Date meant to be read back with the *local* (system) Date getters, so their
// correctness silently depends on the host's own system timezone — they work by accident
// when the system TZ happens to be UTC (true in Cloudflare Workers) but produce wrong
// results on a dev machine or browser whose local TZ differs (notably, one already set to
// America/Chicago, which broke recurrence expansion in local testing here). Everything
// below reads/writes via UTC getters only, so behavior never depends on the caller's
// system timezone — required since this also has to run correctly in end users' browsers.

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

/** Wall-clock date/time components of a real UTC instant, as observed in `timeZone`. */
export function getZonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = Object.fromEntries(
    getFormatter(timeZone)
      .formatToParts(date)
      .map((p) => [p.type, p.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: parts.hour === "24" ? 0 : Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/**
 * A "floating" Date whose UTC getters equal the wall-clock time in `timeZone` for the given
 * real instant. Useful for feeding into calendar-math libraries (like rrule) that always
 * read via UTC getters.
 */
export function utcToFloating(date: Date, timeZone: string): Date {
  const p = getZonedParts(date, timeZone);
  return new Date(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, date.getUTCMilliseconds()));
}

/**
 * The real UTC instant for wall-clock numbers (read via UTC getters of `floating`) that are
 * meant to represent local time in `timeZone`. Inverse of utcToFloating. Converges in two
 * passes even across DST transitions.
 */
export function floatingToUtc(floating: Date, timeZone: string): Date {
  let guess = floating.getTime();
  for (let i = 0; i < 2; i++) {
    const asZoned = utcToFloating(new Date(guess), timeZone);
    const diff = asZoned.getTime() - floating.getTime();
    guess -= diff;
  }
  return new Date(guess);
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * "YYYY-MM-DDTHH:mm:ss" wall-clock in `timeZone`, with no offset/Z suffix. Useful for
 * feeding a calendar UI library (like FullCalendar) that doesn't itself understand named
 * IANA zones: give it these naive strings and let it render them literally, rather than
 * asking the library to convert timezones itself.
 */
export function toNaiveIsoString(date: Date, timeZone: string): string {
  const p = getZonedParts(date, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

/** The real UTC instant for explicit wall-clock components in `timeZone`. */
export function zonedWallClockToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): Date {
  return floatingToUtc(new Date(Date.UTC(year, month - 1, day, hour, minute, second)), timeZone);
}
